import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAdminSession } from "../context/AdminSession";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast, Toaster } from "@/components/ui/sonner";
import {
  Radio,
  LayoutGrid,
  Users,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  X,
  Upload,
  Image as ImageIcon,
  Gauge,
  Play,
  Pause,
  RotateCcw,
} from "lucide-react";

const EMPTY = {
  team_a: "",
  team_b: "",
  start_time: "",
  league: "",
  embed_link: "",
  thumbnail_url: "",
  description: "",
  status: "upcoming",
  source_type: "",
  team_a_logo: "",
  team_b_logo: "",
  team_a_color: "#007AFF",
  team_b_color: "#FF3B30",
  scoreboard_enabled: false,
};

const STATUS_STYLES = {
  live: "bg-[#007AFF] text-white",
  upcoming: "bg-[#007AFF] text-white",
  finished: "bg-white/10 text-white/70",
};

export default function AdminDashboard() {
  const { user, signedIn, loading, logout } = useAdminSession();
  const navigate = useNavigate();
  const [tab, setTab] = useState("games");
  const [games, setGames] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [modal, setModal] = useState(null); // {game} for edit, {} for new
  const [adminModal, setAdminModal] = useState(false);
  const [scoreModal, setScoreModal] = useState(null);

  const handleSessionError = useCallback(
    (err) => {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("Your admin session has expired. Please sign in again.");
        logout();
        return true;
      }
      if (status === 403) {
        toast.error("YSV administrator access is required.");
        navigate("/");
        return true;
      }
      return false;
    },
    [logout, navigate],
  );

  const loadGames = useCallback(() => {
    api
      .get("/games")
      .then(({ data }) => setGames(data))
      .catch((err) => {
        if (!handleSessionError(err)) toast.error("Could not load games");
      });
  }, [handleSessionError]);
  const loadAdmins = useCallback(() => {
    api
      .get("/admin/users")
      .then(({ data }) => setAdmins(data))
      .catch((err) => {
        if (!handleSessionError(err)) toast.error("Could not load admins");
      });
  }, [handleSessionError]);

  useEffect(() => {
    if (!loading && !user) navigate(signedIn ? "/" : "/sign-in");
  }, [loading, user, signedIn, navigate]);

  useEffect(() => {
    if (user) {
      loadGames();
      loadAdmins();
    }
  }, [user, loadGames, loadAdmins]);

  if (loading || !user)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] font-display uppercase tracking-widest text-white/40">
        Loading…
      </div>
    );

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this game?")) return;
    try {
      await api.delete(`/games/${id}`);
      toast.success("Game deleted");
      loadGames();
    } catch (err) {
      if (!handleSessionError(err)) toast.error("Could not delete game");
    }
  };

  const handleDeleteAdmin = async (admin) => {
    if (!window.confirm(`Remove admin ${admin.email}? They will lose access immediately.`)) return;
    try {
      await api.delete(`/admin/users/${admin.id}`);
      toast.success("Admin removed");
      loadAdmins();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove admin");
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 bg-[#0A0A0A] md:grid-cols-[250px_1fr]">
      <Toaster />
      {/* Sidebar */}
      <aside className="border-r border-white/10 bg-[#141414] md:min-h-screen">
        <div className="flex items-center gap-2 border-b border-white/10 p-6">
          <span className="flex h-8 w-8 items-center justify-center bg-[#007AFF]">
            <Radio className="h-5 w-5 text-white" />
          </span>
          <span className="font-display text-xl font-black uppercase tracking-tighter text-white">
            YSV<span className="text-[#007AFF]">Live</span>
          </span>
        </div>
        <nav className="flex flex-col p-3">
          <SideBtn active={tab === "games"} onClick={() => setTab("games")} icon={LayoutGrid} label="Games" testid="tab-games" />
          <SideBtn active={tab === "admins"} onClick={() => setTab("admins")} icon={Users} label="Admins" testid="tab-admins" />
          <Link
            to="/"
            className="mt-1 flex items-center gap-3 px-4 py-3 font-display text-sm font-bold uppercase tracking-wider text-white/50 transition-colors hover:text-white"
          >
            <Radio className="h-4 w-4" /> View Site
          </Link>
          <button
            data-testid="logout-button"
            onClick={async () => {
              await logout();
              navigate("/");
            }}
            className="flex items-center gap-3 px-4 py-3 font-display text-sm font-bold uppercase tracking-wider text-white/50 transition-colors hover:text-[#007AFF]"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </nav>
      </aside>

      {/* Main */}
      <main className="p-6 md:p-10">
        {tab === "games" ? (
          <>
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="font-display text-3xl font-black uppercase tracking-tighter text-white md:text-4xl">
                  Games
                </h1>
                <p className="text-sm text-white/50">Logged in as {user.email}</p>
              </div>
              <button
                data-testid="add-game-button"
                onClick={() => setModal({ ...EMPTY })}
                className="inline-flex items-center gap-2 bg-[#007AFF] px-5 py-3 font-display text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#0062cc]"
              >
                <Plus className="h-4 w-4" /> Add Game
              </button>
            </div>

            <div className="border border-white/10">
              <table className="w-full text-left" data-testid="games-table">
                <thead className="border-b border-white/10 bg-[#141414]">
                  <tr className="font-display text-xs uppercase tracking-widest text-white/50">
                    <th className="p-4">Matchup</th>
                    <th className="hidden p-4 md:table-cell">League</th>
                    <th className="hidden p-4 lg:table-cell">Time</th>
                    <th className="p-4">Status</th>
                    <th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {games.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-white/40">
                        No games yet. Click "Add Game" to create one.
                      </td>
                    </tr>
                  ) : (
                    games.map((g) => (
                      <tr key={g.id} className="border-b border-white/5" data-testid={`game-row-${g.id}`}>
                        <td className="p-4 font-display text-lg font-bold uppercase tracking-tight text-white">
                          {g.team_a} <span className="text-white/30">vs</span> {g.team_b}
                        </td>
                        <td className="hidden p-4 text-white/60 md:table-cell">{g.league || "—"}</td>
                        <td className="hidden p-4 text-white/60 lg:table-cell">
                          {g.start_time ? new Date(g.start_time).toLocaleString() : "—"}
                        </td>
                        <td className="p-4">
                          <span className={`px-2.5 py-1 font-display text-xs font-bold uppercase tracking-widest ${STATUS_STYLES[g.status]}`}>
                            {g.status}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            <button
                              data-testid={`live-control-${g.id}`}
                              onClick={() => setScoreModal({ ...g })}
                              title="Scoreboard live control"
                              className="border border-white/10 p-2 text-white/70 transition-colors hover:border-[#007AFF] hover:text-[#007AFF]"
                            >
                              <Gauge className="h-4 w-4" />
                            </button>
                            <button
                              data-testid={`edit-game-${g.id}`}
                              onClick={() => setModal({ ...g })}
                              className="border border-white/10 p-2 text-white/70 transition-colors hover:border-[#007AFF] hover:text-white"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              data-testid={`delete-game-${g.id}`}
                              onClick={() => handleDelete(g.id)}
                              className="border border-white/10 p-2 text-white/70 transition-colors hover:border-[#007AFF] hover:text-[#007AFF]"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <AdminsTab admins={admins} onAdd={() => setAdminModal(true)} currentUserId={user.id} onDelete={handleDeleteAdmin} />
        )}
      </main>

      {modal && (
        <GameModal
          initial={modal}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            loadGames();
          }}
        />
      )}
      {adminModal && (
        <AdminModal
          onClose={() => setAdminModal(false)}
          onSaved={() => {
            setAdminModal(false);
            loadAdmins();
          }}
        />
      )}
      {scoreModal && (
        <LiveControlModal
          game={scoreModal}
          onClose={() => setScoreModal(null)}
          onSaved={() => {
            setScoreModal(null);
            loadGames();
          }}
        />
      )}
    </div>
  );
}

const SideBtn = ({ active, onClick, icon: Icon, label, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`flex items-center gap-3 px-4 py-3 font-display text-sm font-bold uppercase tracking-wider transition-colors ${
      active ? "bg-[#0A0A0A] text-white" : "text-white/50 hover:text-white"
    }`}
  >
    <Icon className="h-4 w-4" /> {label}
  </button>
);

function AdminsTab({ admins, onAdd, currentUserId, onDelete }) {
  return (
    <>
      <div className="mb-8 flex items-center justify-between">
        <h1 className="font-display text-3xl font-black uppercase tracking-tighter text-white md:text-4xl">
          Admin Accounts
        </h1>
        <button
          data-testid="add-admin-button"
          onClick={onAdd}
          className="inline-flex items-center gap-2 bg-[#007AFF] px-5 py-3 font-display text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#0062cc]"
        >
          <Plus className="h-4 w-4" /> Add Admin
        </button>
      </div>
      <div className="border border-white/10">
        <table className="w-full text-left">
          <thead className="border-b border-white/10 bg-[#141414]">
            <tr className="font-display text-xs uppercase tracking-widest text-white/50">
              <th className="p-4">Name</th>
              <th className="p-4">Email</th>
              <th className="p-4">Role</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((a) => {
              const isSelf = a.id === currentUserId;
              return (
                <tr key={a.id} className="border-b border-white/5" data-testid={`admin-row-${a.id}`}>
                  <td className="p-4 text-white">
                    {a.name} {isSelf && <span className="ml-1 text-xs text-white/40">(you)</span>}
                  </td>
                  <td className="p-4 text-white/60">{a.email}</td>
                  <td className="p-4">
                    <span className="bg-white/10 px-2.5 py-1 font-display text-xs font-bold uppercase tracking-widest text-white/70">
                      {a.role}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end">
                      <button
                        data-testid={`delete-admin-${a.id}`}
                        onClick={() => onDelete(a)}
                        disabled={isSelf}
                        title={isSelf ? "You can't remove your own account" : "Remove admin"}
                        className="border border-white/10 p-2 text-white/70 transition-colors enabled:hover:border-[#FF3B30] enabled:hover:text-[#FF3B30] disabled:cursor-not-allowed disabled:opacity-30"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

const inputCls =
  "w-full border border-white/10 bg-[#0A0A0A] px-4 py-2.5 text-white placeholder-white/30 focus:border-[#007AFF] focus:outline-none focus:ring-1 focus:ring-[#007AFF]";
const labelCls =
  "mb-1.5 block font-display text-xs font-bold uppercase tracking-[0.2em] text-white/60";

const LogoUploadBox = ({ value, color, field, onUpload, uploading, onClear }) => (
  <label
    data-testid={`upload-${field}-button`}
    className="relative flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center overflow-hidden border border-white/15 bg-[#0A0A0A] transition-colors hover:border-[#007AFF]"
    title="Upload logo"
  >
    {value ? (
      <img src={value} alt="logo" className="h-full w-full object-contain" onError={(e) => (e.currentTarget.style.display = "none")} />
    ) : uploading ? (
      <span className="text-[0.55rem] uppercase text-white/50">…</span>
    ) : (
      <Upload className="h-4 w-4 text-white/50" />
    )}
    <input type="file" accept="image/*" data-testid={`${field}-file-input`} onChange={onUpload} disabled={uploading} className="hidden" />
  </label>
);


function GameModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [cfKind, setCfKind] = useState("live_input");
  const [cfItems, setCfItems] = useState([]);
  const [cfLoading, setCfLoading] = useState(false);
  const [cfError, setCfError] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [diag, setDiag] = useState(null);
  const [uploadingField, setUploadingField] = useState(null);
  const isEdit = !!initial.id;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const makeUpload = (field) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingField(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/thumbnail", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, [field]: `${API}/files/${data.path}` }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingField(null);
      e.target.value = "";
    }
  };

  useEffect(() => {
    setCfLoading(true);
    setCfError(null);
    setAuthError(null);
    const path = cfKind === "video" ? "/cloudflare/videos" : "/cloudflare/live-inputs";
    api
      .get(path)
      .then(({ data }) => setCfItems(data))
      .catch((err) => {
        setCfItems([]);
        const status = err.response?.status;
        const detail = formatApiErrorDetail(err.response?.data?.detail);
        if (status === 401 || status === 403 || status === 503) {
          // Not a Cloudflare problem — the admin sign-in session expired / lacks access.
          setAuthError(detail || "Your admin session has expired. Please sign in again.");
        } else {
          setCfError(detail || "Could not reach Cloudflare");
          api.get("/cloudflare/diagnose").then(({ data }) => setDiag(data)).catch(() => {});
        }
      })
      .finally(() => setCfLoading(false));
  }, [cfKind]);

  const pickCf = (e) => {
    const uid = e.target.value;
    const item = cfItems.find((i) => i.uid === uid);
    if (!item) return;
    setForm((f) => ({
      ...f,
      embed_link: uid,
      source_type: cfKind,
      thumbnail_url: item.thumbnail || f.thumbnail_url,
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      team_a: form.team_a,
      team_b: form.team_b,
      start_time: form.start_time,
      league: form.league,
      embed_link: form.embed_link,
      thumbnail_url: form.thumbnail_url,
      description: form.description,
      status: form.status,
      source_type: form.source_type || "",
      team_a_logo: form.team_a_logo || "",
      team_b_logo: form.team_b_logo || "",
      team_a_color: form.team_a_color || "#007AFF",
      team_b_color: form.team_b_color || "#FF3B30",
      scoreboard_enabled: !!form.scoreboard_enabled,
    };
    try {
      if (isEdit) await api.put(`/games/${initial.id}`, payload);
      else await api.post("/games", payload);
      toast.success(isEdit ? "Game updated" : "Game created");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay onClose={onClose}>
      <form onSubmit={save} data-testid="game-form" className="space-y-4">
        <ModalHeader title={isEdit ? "Edit Game" : "Add Game"} onClose={onClose} />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Team A</label>
            <input data-testid="input-team-a" required value={form.team_a} onChange={set("team_a")} className={inputCls} placeholder="Eagles" />
          </div>
          <div>
            <label className={labelCls}>Team B</label>
            <input data-testid="input-team-b" required value={form.team_b} onChange={set("team_b")} className={inputCls} placeholder="Titans" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Team A Logo</label>
            <div className="flex gap-2">
              <LogoUploadBox value={form.team_a_logo} color={form.team_a_color} field="team_a_logo" onUpload={makeUpload("team_a_logo")} uploading={uploadingField === "team_a_logo"} onClear={() => setForm((f) => ({ ...f, team_a_logo: "" }))} />
              <input data-testid="input-team-a-color" type="color" value={form.team_a_color} onChange={set("team_a_color")} className="h-11 w-11 shrink-0 cursor-pointer border border-white/10 bg-[#0A0A0A]" title="Team A color" />
            </div>
            <input data-testid="input-team-a-logo" value={form.team_a_logo} onChange={set("team_a_logo")} className={`${inputCls} mt-2`} placeholder="…or paste logo URL" />
          </div>
          <div>
            <label className={labelCls}>Team B Logo</label>
            <div className="flex gap-2">
              <LogoUploadBox value={form.team_b_logo} color={form.team_b_color} field="team_b_logo" onUpload={makeUpload("team_b_logo")} uploading={uploadingField === "team_b_logo"} onClear={() => setForm((f) => ({ ...f, team_b_logo: "" }))} />
              <input data-testid="input-team-b-color" type="color" value={form.team_b_color} onChange={set("team_b_color")} className="h-11 w-11 shrink-0 cursor-pointer border border-white/10 bg-[#0A0A0A]" title="Team B color" />
            </div>
            <input data-testid="input-team-b-logo" value={form.team_b_logo} onChange={set("team_b_logo")} className={`${inputCls} mt-2`} placeholder="…or paste logo URL" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Date & Time</label>
            <input data-testid="input-start-time" type="datetime-local" required value={form.start_time} onChange={set("start_time")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>League / Category</label>
            <input data-testid="input-league" value={form.league} onChange={set("league")} className={inputCls} placeholder="U16 Football" />
          </div>
        </div>
        <div className="border border-white/10 bg-[#0A0A0A] p-3">
          <label className={labelCls}>Cloudflare Stream Source</label>
          <div className="mb-2 flex gap-2">
            <button
              type="button"
              data-testid="cf-kind-live"
              onClick={() => setCfKind("live_input")}
              className={`flex-1 py-2 font-display text-xs font-bold uppercase tracking-widest transition-colors ${
                cfKind === "live_input" ? "bg-[#007AFF] text-white" : "border border-white/10 text-white/60 hover:text-white"
              }`}
            >
              Live Inputs
            </button>
            <button
              type="button"
              data-testid="cf-kind-video"
              onClick={() => setCfKind("video")}
              className={`flex-1 py-2 font-display text-xs font-bold uppercase tracking-widest transition-colors ${
                cfKind === "video" ? "bg-[#007AFF] text-white" : "border border-white/10 text-white/60 hover:text-white"
              }`}
            >
              Recorded Videos
            </button>
          </div>
          <select
            data-testid="cf-picker"
            value={cfItems.some((i) => i.uid === form.embed_link) ? form.embed_link : ""}
            onChange={pickCf}
            className={inputCls}
          >
            <option value="">
              {cfLoading ? "Loading from Cloudflare…" : `Select a ${cfKind === "video" ? "video" : "live input"}…`}
            </option>
            {cfItems.map((i) => (
              <option key={i.uid} value={i.uid}>
                {i.name}
                {i.state && i.state !== "ready" ? ` (${i.state})` : ""}
              </option>
            ))}
          </select>
          {authError && (
            <div data-testid="cf-auth-error" className="mt-2 border border-[#007AFF]/40 bg-[#007AFF]/10 p-3 text-xs">
              <p className="font-display font-bold uppercase tracking-widest text-[#007AFF]">Session expired</p>
              <p className="mt-1 text-white/70">{authError}</p>
              <a
                href="/sign-in"
                data-testid="cf-signin-link"
                onClick={() => {
                  try {
                    localStorage.removeItem("ysvlive_token");
                  } catch (e) {}
                }}
                className="mt-2 inline-block bg-[#007AFF] px-4 py-2 font-display text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#0062cc]"
              >
                Sign in again
              </a>
            </div>
          )}
          {cfError && (
            <div data-testid="cf-error" className="mt-2 border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-3 text-xs">
              <p className="font-display font-bold uppercase tracking-widest text-[#FF3B30]">Cloudflare unreachable</p>
              <p className="mt-1 text-white/70">{cfError}</p>
              {diag && (
                <div className="mt-2 space-y-1 text-white/70">
                  <p>
                    This server's outbound IP is{" "}
                    <span data-testid="diag-ip" className="font-mono font-bold text-white">{diag.outbound_ip}</span>.
                  </p>
                  <p>Add this exact IP to your Cloudflare API token's "Client IP Address Filtering" allowlist (My Profile → API Tokens → edit token), then save and reopen this dialog.</p>
                </div>
              )}
            </div>
          )}
          <label className="mt-3 mb-1 block font-display text-[0.6rem] font-bold uppercase tracking-[0.2em] text-white/40">
            Or paste an embed link / UID manually
          </label>
          <input data-testid="input-embed" value={form.embed_link} onChange={set("embed_link")} className={inputCls} placeholder="video UID, iframe URL, or full <iframe> tag" />
        </div>
        <div>
          <label className={labelCls}>Thumbnail Image</label>
          <div className="flex items-start gap-3">
            {form.thumbnail_url ? (
              <img
                src={form.thumbnail_url}
                alt="thumbnail preview"
                data-testid="thumbnail-preview"
                className="h-16 w-28 shrink-0 border border-white/10 object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div className="flex h-16 w-28 shrink-0 items-center justify-center border border-dashed border-white/15 text-white/30">
                <ImageIcon className="h-5 w-5" />
              </div>
            )}
            <div className="flex-1 space-y-2">
              <label
                data-testid="upload-thumbnail-button"
                className="inline-flex cursor-pointer items-center gap-2 border border-white/20 px-4 py-2 font-display text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#007AFF]"
              >
                <Upload className="h-4 w-4" />
                {uploadingField === "thumbnail_url" ? "Uploading…" : "Upload Image"}
                <input type="file" accept="image/*" data-testid="thumbnail-file-input" onChange={makeUpload("thumbnail_url")} disabled={uploadingField === "thumbnail_url"} className="hidden" />
              </label>
              <input data-testid="input-thumbnail" value={form.thumbnail_url} onChange={set("thumbnail_url")} className={inputCls} placeholder="…or paste an image URL" />
            </div>
          </div>
        </div>
        <div>
          <label className={labelCls}>Description</label>
          <textarea data-testid="input-description" value={form.description} onChange={set("description")} rows={3} className={inputCls} placeholder="Game details…" />
        </div>
        <label className="flex cursor-pointer items-center justify-between border border-white/10 bg-[#0A0A0A] p-3">
          <span>
            <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white/80">Scoreboard Overlay</span>
            <span className="block text-xs text-white/40">Show live score & clock over the player</span>
          </span>
          <input
            type="checkbox"
            data-testid="input-scoreboard-enabled"
            checked={!!form.scoreboard_enabled}
            onChange={(e) => setForm({ ...form, scoreboard_enabled: e.target.checked })}
            className="h-5 w-5 cursor-pointer accent-[#007AFF]"
          />
        </label>
        <div>
          <label className={labelCls}>Status</label>
          <select data-testid="input-status" value={form.status} onChange={set("status")} className={inputCls}>
            <option value="upcoming">Upcoming</option>
            <option value="live">Live Now</option>
            <option value="finished">Finished (Library)</option>
          </select>
          {form.source_type === "live_input" && (
            <p className="mt-2 text-xs text-[#007AFF]">
              Auto-status is ON: this game flips to LIVE when the Cloudflare input starts broadcasting and moves to the Library replay when it ends.
            </p>
          )}
        </div>
        <button
          type="submit"
          data-testid="save-game-button"
          disabled={busy}
          className="w-full bg-[#007AFF] py-3 font-display text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#0062cc] disabled:opacity-50"
        >
          {busy ? "Saving…" : isEdit ? "Update Game" : "Create Game"}
        </button>
      </form>
    </Overlay>
  );
}

function AdminModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Admin created");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create admin");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay onClose={onClose}>
      <form onSubmit={save} data-testid="admin-form" className="space-y-4">
        <ModalHeader title="Add Admin" onClose={onClose} />
        <div>
          <label className={labelCls}>Name</label>
          <input data-testid="input-admin-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Doe" />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input data-testid="input-admin-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@ysvlive.com" />
        </div>
        <div>
          <label className={labelCls}>Password</label>
          <input data-testid="input-admin-password" type="password" required value={form.password} onChange={set("password")} className={inputCls} placeholder="••••••••" />
        </div>
        <button
          type="submit"
          data-testid="save-admin-button"
          disabled={busy}
          className="w-full bg-[#007AFF] py-3 font-display text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#0062cc] disabled:opacity-50"
        >
          {busy ? "Creating…" : "Create Admin"}
        </button>
      </form>
    </Overlay>
  );
}

function mmss(total) {
  const s = Math.max(0, Math.floor(total));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

function LiveControlModal({ game, onClose, onSaved }) {
  const [enabled, setEnabled] = useState(!!game.scoreboard_enabled);
  const [scoreA, setScoreA] = useState(game.score_a || 0);
  const [scoreB, setScoreB] = useState(game.score_b || 0);
  const [period, setPeriod] = useState(game.period || "");
  const [running, setRunning] = useState(!!game.clock_running);
  const [seconds, setSeconds] = useState(() => {
    let s = game.clock_seconds || 0;
    if (game.clock_running && game.clock_updated_at) {
      const elapsed = (Date.now() - new Date(game.clock_updated_at).getTime()) / 1000;
      s = Math.max(0, s - elapsed);
    }
    return Math.round(s);
  });
  const [minInput, setMinInput] = useState(Math.floor((game.clock_seconds || 0) / 60));
  const [secInput, setSecInput] = useState((game.clock_seconds || 0) % 60);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [running]);

  const push = async (patch, opts = {}) => {
    setBusy(true);
    try {
      await api.put(`/games/${game.id}/scoreboard`, patch);
      if (!opts.silent) toast.success("Scoreboard updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const changeScore = (team, delta) => {
    if (team === "a") {
      const v = Math.max(0, scoreA + delta);
      setScoreA(v);
      push({ score_a: v }, { silent: true });
    } else {
      const v = Math.max(0, scoreB + delta);
      setScoreB(v);
      push({ score_b: v }, { silent: true });
    }
  };

  const startClock = () => {
    setRunning(true);
    push({ clock_seconds: Math.round(seconds), clock_running: true }, { silent: true });
  };
  const pauseClock = () => {
    setRunning(false);
    push({ clock_seconds: Math.round(seconds), clock_running: false }, { silent: true });
  };
  const setClock = () => {
    const total = Number(minInput) * 60 + Number(secInput);
    setSeconds(total);
    setRunning(false);
    push({ clock_seconds: total, clock_running: false });
  };

  const saveAll = async () => {
    await push({
      scoreboard_enabled: enabled,
      score_a: scoreA,
      score_b: scoreB,
      period,
      clock_seconds: Math.round(seconds),
      clock_running: running,
    });
    onSaved();
  };

  return (
    <Overlay onClose={onClose}>
      <div data-testid="live-control" className="space-y-5">
        <ModalHeader title="Live Control" onClose={onClose} />
        <p className="-mt-3 font-display text-lg font-bold uppercase tracking-tight text-white/80">
          {game.team_a} <span className="text-white/30">vs</span> {game.team_b}
        </p>

        <label className="flex cursor-pointer items-center justify-between border border-white/10 bg-[#0A0A0A] p-3">
          <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white/80">Show Scoreboard On Player</span>
          <input
            type="checkbox"
            data-testid="sb-enabled-toggle"
            checked={enabled}
            onChange={(e) => {
              setEnabled(e.target.checked);
              push({ scoreboard_enabled: e.target.checked }, { silent: true });
            }}
            className="h-5 w-5 cursor-pointer accent-[#007AFF]"
          />
        </label>

        {/* Scores */}
        <div className="grid grid-cols-2 gap-4">
          {[["a", game.team_a, scoreA], ["b", game.team_b, scoreB]].map(([k, name, val]) => (
            <div key={k} className="border border-white/10 p-4 text-center">
              <p className="mb-2 truncate font-display text-sm font-bold uppercase tracking-widest text-white/70">{name}</p>
              <p data-testid={`sb-score-${k}`} className="font-display text-5xl font-black text-white">{val}</p>
              <div className="mt-3 flex justify-center gap-2">
                <button data-testid={`sb-score-${k}-minus`} onClick={() => changeScore(k, -1)} className="h-9 w-9 border border-white/15 font-display text-lg font-bold text-white hover:border-[#FF3B30] hover:text-[#FF3B30]">−</button>
                <button data-testid={`sb-score-${k}-plus`} onClick={() => changeScore(k, 1)} className="h-9 w-9 border border-white/15 font-display text-lg font-bold text-white hover:border-[#007AFF] hover:bg-[#007AFF]">+</button>
              </div>
            </div>
          ))}
        </div>

        {/* Period */}
        <div>
          <label className={labelCls}>Period / Quarter</label>
          <input data-testid="sb-period" value={period} onChange={(e) => setPeriod(e.target.value)} onBlur={() => push({ period }, { silent: true })} className={inputCls} placeholder="Q3 / 2nd Half / Set 2" />
        </div>

        {/* Clock */}
        <div className="border border-white/10 p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className={labelCls + " mb-0"}>Game Clock</span>
            <span data-testid="sb-clock" className="font-display text-3xl font-black tabular-nums text-white">{mmss(seconds)}</span>
          </div>
          <div className="flex items-center gap-2">
            {running ? (
              <button data-testid="sb-pause" onClick={pauseClock} className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-display text-xs font-bold uppercase tracking-widest text-white hover:bg-[#FF3B30]">
                <Pause className="h-4 w-4" /> Pause
              </button>
            ) : (
              <button data-testid="sb-start" onClick={startClock} className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-display text-xs font-bold uppercase tracking-widest text-white hover:bg-[#007AFF]">
                <Play className="h-4 w-4" /> Start
              </button>
            )}
            <div className="ml-auto flex items-center gap-1">
              <input data-testid="sb-min" type="number" min="0" value={minInput} onChange={(e) => setMinInput(e.target.value)} className="w-16 border border-white/10 bg-[#0A0A0A] px-2 py-2 text-center text-white" />
              <span className="text-white/40">:</span>
              <input data-testid="sb-sec" type="number" min="0" max="59" value={secInput} onChange={(e) => setSecInput(e.target.value)} className="w-16 border border-white/10 bg-[#0A0A0A] px-2 py-2 text-center text-white" />
              <button data-testid="sb-set-clock" onClick={setClock} className="ml-1 inline-flex items-center gap-1 border border-white/20 px-3 py-2 font-display text-xs font-bold uppercase tracking-widest text-white hover:bg-[#007AFF]">
                <RotateCcw className="h-3.5 w-3.5" /> Set
              </button>
            </div>
          </div>
          <p className="mt-2 text-xs text-white/40">Changes push live to viewers instantly. The clock keeps counting down on their screens.</p>
        </div>

        <button
          onClick={saveAll}
          data-testid="sb-save"
          disabled={busy}
          className="w-full bg-[#007AFF] py-3 font-display text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#0062cc] disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save & Close"}
        </button>
      </div>
    </Overlay>
  );
}


const Overlay = ({ children, onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm sm:items-center"
    onClick={onClose}
  >
    <div
      className="my-auto max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto border border-white/10 bg-[#141414] p-6"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  </div>
);

const ModalHeader = ({ title, onClose }) => (
  <div className="mb-2 flex items-center justify-between">
    <h2 className="font-display text-2xl font-black uppercase tracking-tighter text-white">{title}</h2>
    <button type="button" onClick={onClose} data-testid="close-modal" className="text-white/50 hover:text-white">
      <X className="h-5 w-5" />
    </button>
  </div>
);
